import React from "react";

import { autobind, throttleToAnimationFrame } from "@use/decorators";

import { GridColumn, GridModel } from "@courses-pm2/api";

/**
 * Specifies the assumed width of a grid, given in CSS pixels, which the editors
 * are operating with when creating content. We're using this value to determine
 * the desired minimum width of each column within the grid.
 */
const EXPECTED_GRID_WIDTH = 860;

/**
 * Specifies the default minimum column width (this will limit minimum width of
 * a column, so that it will never exceed this value).
 */
const MIN_COLUMN_WIDTH = 250;

export type GridComponentProps = {
    /**
     * The model contains all of the content to render within each instance of
     * the component.
     */
    model: GridModel;
};

type GridComponentState = {
    /**
     * Specifies whether or not the columns of the grid should break in order
     * to improve rendering on mobile devices
     */
    shouldColumnsBreak: boolean;

    /**
     * Specifies the total size of the grid. I.e. accumulating all size of the
     * grid.
     */
    gridSize?: number;
};

/**
 * Root of the render tree for the standard image-component used to render
 * image elements hosted within PM2.
 */
export class GridComponent extends React.PureComponent<GridComponentProps, GridComponentState> {
    /**
     * Setup initial state
     */
    public state: GridComponentState = {
        shouldColumnsBreak: false,
    };

    /**
     * Contains a reference to the grid wrapper, in order to be able to properly
     * determine dimensions of the grid.
     */
    protected readonly gridRef: React.RefObject<HTMLDivElement | null> = React.createRef();

    /**
     * As soon as the component mounts, be sure to properly determine dimensions
     * and bind event listeners.
     */
    public componentDidMount(): void {
        this.updateGridDimensions();

        window.addEventListener("resize", this.onResize);
        window.addEventListener("orientationchange", this.onResize);
    }

    /**
     * Update grid dimensions every time props are updated, since new columns
     * potentially could have been added.
     */
    public componentDidUpdate(): void {
        this.updateGridDimensions();
    }

    /**
     * Unbind event listeners on unmount
     */
    public componentWillUnmount(): void {
        window.removeEventListener("resize", this.onResize);
        window.removeEventListener("orientationchange", this.onResize);
    }

    /**
     * Primary render method.
     */
    public render(): React.JSX.Element {
        const { model } = this.props;
        const { shouldColumnsBreak } = this.state;

        return (
            <div className={`grid ${shouldColumnsBreak ? "rows" : "columns"}`} ref={this.gridRef}>
                {model.columns.map((column, index) => this.renderColumn(column, index))}

                <style jsx>{`
                    .grid {
                        &.columns {
                            /**
                             * If the grid has enough space to render its content in columns,
                             * then use flex to achieve this.
                             */
                            display: flex;
                            justify-content: space-around;

                            /**
                             * Apply negative margins in order to accomodate
                             * for first columns padding left and last items
                             * padding-right
                             */
                            margin: 0 -15px 30px;
                        }

                        &.rows {
                            /**
                             * If not enough space is available, then columns
                             * will be rendered as rows in order to allow more
                             * space.
                             */
                            display: block;
                            margin: 0 0 30px;
                        }
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Renderer that'll render a single column of the grid.
     */
    protected renderColumn(column: GridColumn, index: number): React.ReactNode {
        // If gridSize hasn't been determined yet, then bail out.
        if (!this.state.gridSize) {
            return;
        }

        // Determine how wide the column should be based on model or if full width
        // should be forced.
        const columnWidth = this.state.shouldColumnsBreak
            ? "100%"
            : `${column.size / this.state.gridSize * 100}%`;

        return (
            <section
                key={index}
                className={`${this.state.shouldColumnsBreak ? "asRow" : ""}`}
                style={{
                    width: columnWidth,
                }}
            >
                {this.props.model.renderColumn(index)}

                <style jsx>{`
                    section {
                        /** Setup spacing between columns */
                        box-sizing: border-box;
                        padding: 0 15px;

                        &.asRow {
                            /**
                             * If rendered as row, then the padding must be applied
                             * vertically instead
                             */
                            padding: 0 0 30px;

                            /** ..however, that isn't necessary for the final child */
                            &:last-child {
                                padding-bottom: 0;
                            }
                        }

                        /** Reset margins on custom components nested within */
                        & :global(> .accordion:first-child),
                        & :global(> .audio:first-child),
                        & :global(> .iframe:first-child),
                        & :global(> .image:first-child),
                        & :global(> .richtext:first-child),
                        & :global(> .slider:first-child),
                        & :global(> .video:first-child) {
                            margin-top: 0;
                        }

                        & :global(> .accordion:last-child),
                        & :global(> .audio:last-child),
                        & :global(> .iframe:last-child),
                        & :global(> .image:last-child),
                        & :global(> .richtext:last-child),
                        & :global(> .slider:last-child),
                        & :global(> .video:last-child) {
                            margin-bottom: 0;
                        }
                    }
                `}</style>
            </section>
        );
    }

    /**
     * Helper that updates the grid dimensions based on the currently passed model.
     */
    protected updateGridDimensions = () => {
        // Calculate the total size of each of the available columns within the
        // grid
        const totalSize = this.props.model.columns
            .reduce((reducedValue, column) => reducedValue + column.size, 0);

        // Update sizes, and afterwards determine whether our columns should currently
        // be broken into rows.
        this.setState({ gridSize: totalSize }, () => {
            this.setState({ shouldColumnsBreak: this.shouldColumnsBreak() });
        });
    }

    /**
     * Helper that determines whether or not columns should currently be broken
     * into rows based on the available width of the grid wrapper.
     */
    protected shouldColumnsBreak = (): boolean => {
        const grid = this.gridRef.current;

        // If either the grid, the gridSize or the minColumnSize is available currently
        // then simply assume that we shouldn't break columns.
        if (!grid || !this.state.gridSize) {
            return false;
        }

        // Determine how much padding that have been assigned between columns
        // (there's 30px padding between two columns)
        const totalElementPadding = (this.props.model.columns.length - 1) + 30;

        // Calculate available size within the grid for all of the columns,
        // after having taking the required paddings into account
        const availableSize = grid.clientWidth - totalElementPadding;

        // Iterate over each of the columns, and determine if the size available
        // to it exceeds the minimum expectation
        for (const column of this.props.model.columns) {
            // Calculate the minimum required width for this column, based on
            // the size it would have had initially (but we never require more
            // than the predefined minimum width)
            const minWidth = Math.min(MIN_COLUMN_WIDTH, EXPECTED_GRID_WIDTH / this.state.gridSize * column.size);

            // If there is less space available for this column than the min
            // width we just calculated, then we should break columns
            if (availableSize / this.state.gridSize * column.size < minWidth) {
                return true;
            }
        }

        // If we get here, there's enough room to render each of the columns in
        // the grid in a fitting size!
        return false;
    }

    /**
     * Listener that'll be called every time the viewport is resized in order to
     * determine whether the columns should break into rows due to limited space.
     */
    @autobind()
    @throttleToAnimationFrame()
    protected onResize(): void {
        this.setState({ shouldColumnsBreak: this.shouldColumnsBreak() });
    }
}
